import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  getTaskWorkflow,
  rejectTask,
} from "../../../services/taskServices/TaskWorkflowServices";
import TaskWorkflow from "../../../interfaces/task/workflow/TaskWorkflow";
import { RejectTaskState } from "../../../interfaces/task/Task";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import "./RejectTaskModal.css";

interface OptionType {
  value: string;
  label: string;
}

interface RejectTaskModalProps {
  authToken: string;
  taskId: string;
  workflowItems?: TaskWorkflow[];
  handleShowRejectTaskModal: () => void;
  handleCloseRejectTaskModal: () => void;
  showRejectTaskModal: boolean;
  reload: () => void;
  setLoadingFor: React.Dispatch<React.SetStateAction<string | null>>;
}

const defaultRejectform: RejectTaskState = {
  assignee: null,
  durationInMinutes: 0,
  feedback: "",
};

const RejectTaskModal = ({
  authToken,
  taskId,
  workflowItems,
  handleCloseRejectTaskModal,
  handleShowRejectTaskModal,
  showRejectTaskModal,
  reload,
  setLoadingFor,
}: RejectTaskModalProps) => {
  const [assignmentOptions, setAssignmentOptions] = useState<OptionType[]>([]);
  const [rejectForm, setRejectForm] =
    useState<RejectTaskState>(defaultRejectform);
  const [loading, setLoading] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (workflowItems !== undefined) {
      const maxId = workflowItems.reduce((max, obj) => {
        if (obj.taskApproval !== undefined && obj.stepOrder > max) {
          return obj.stepOrder;
        }
        return max;
      }, 0);

      const maxIdMinusOne = workflowItems.find(
        (obj) => obj.stepOrder === maxId - 1
      )?.taskApproval;

      const previousApprovers = workflowItems.filter(
        (obj) => obj.stepOrder < maxId
      );

      const options = previousApprovers.map((item) => ({
        value: item?.taskApproval?.id?.toString() || "",
        label:
          item.taskApproval?.approver?.firstName &&
          item.taskApproval?.approver?.lastName
            ? `${item.taskApproval.approver.firstName} ${item.taskApproval.approver.lastName} (${item.role.name})`
            : "",
      }));

      if (maxIdMinusOne && options) {
        setAssignmentOptions(options);
      }
    }
  }, [workflowItems]);

  const validateFields = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!rejectForm.assignee) {
      errors.assignee = "Please select an assignee.";
    }

    if (!rejectForm.durationInMinutes || rejectForm.durationInMinutes <= 0) {
      errors.durationInMinutes = "Please enter valid minutes.";
    }

    if (!rejectForm.feedback.trim()) {
      errors.feedback = "Feedback is required.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const HandleClickReject = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateFields()) {
      return;
    }

    if (!rejectForm.assignee) return;

    setLoadingFor("ApproveReject");
    setLoading(true);
    rejectTask(
      taskId,
      rejectForm.feedback,
      rejectForm?.assignee?.id,
      rejectForm.durationInMinutes,
      authToken
    )
      .then((res) => {
        toast.success("Task Rejected Successfully");
        reload();
        handleCloseRejectTaskModal();
        setRejectForm(defaultRejectform);
        setFormErrors({});
      })
      .catch((err) => {
        toast.error(err.message);
        console.log("Error:", err);
      })
      .finally(() => {
        setLoadingFor("");
        setLoading(false);
      });
  };

  const handleAssignmentChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const assignee = event.target.value;
    const newAssignee = workflowItems?.find(
      (wi) => wi.taskApproval?.id.toString() === assignee
    )?.taskApproval;
    if (newAssignee) {
      setRejectForm((prev) => ({
        ...prev,
        assignee: newAssignee,
      }));
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = event.target;

    setRejectForm((prev) => ({
      ...prev,
      [name]: type === "number" ? Number(value) : value,
    }));
  };

  if (!showRejectTaskModal) return null;

  return (
    <>
      <div className="modal-activity-reject-overlay">
        <div className="modal-activity-reject-container max-w-[480px]">
          <div className="modal-activity-reject-header">
            <h2 className="modal-activity-reject-title">Reject Task</h2>
            <button
              className="modal-activity-reject-close"
              onClick={handleCloseRejectTaskModal}
              type="button"
            >
              <FontAwesomeIcon icon={faTimes} className="w-[1rem] h-[1rem]" />
            </button>
          </div>

          <form
            onSubmit={HandleClickReject}
            className="modal-activity-reject-form"
          >
            <div className="modal-activity-reject-body">
              <div className="form-group">
                <label
                  htmlFor="assignee"
                  className="form-label-reject !text-[1rem]"
                >
                  Assign to<span className="required ">*</span>
                </label>
                <select
                  id="assignee"
                  name="assignee"
                  onChange={handleAssignmentChange}
                  className="form-select !px-[0.75rem] !text-[#717182]"
                  value={rejectForm.assignee?.id.toString() || ""}
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {assignmentOptions.map((item, index) => (
                    <option value={item.value} key={index}>
                      {item.label}
                    </option>
                  ))}
                </select>
                {formErrors.assignee && (
                  <span className="error-message">{formErrors.assignee}</span>
                )}
              </div>

              <div className="form-group">
                <label
                  htmlFor="durationInMinutes"
                  className="form-label-reject !text-[1rem]"
                >
                  How long did it take you to review? (in minutes)
                  <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="durationInMinutes"
                  name="durationInMinutes"
                  onChange={handleChange}
                  value={rejectForm.durationInMinutes || ""}
                  className="form-input"
                  min="0"
                  placeholder="0"
                />
                {formErrors.durationInMinutes && (
                  <span className="error-message">
                    {formErrors.durationInMinutes}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="feedback" className="form-label-reject">
                  Feedback<span className="required">*</span>
                </label>
                <textarea
                  id="feedback"
                  name="feedback"
                  placeholder="A brief description of the reason for rejection..."
                  onChange={handleChange}
                  value={rejectForm.feedback}
                  className="form-textarea"
                  rows={4}
                />
                {formErrors.feedback && (
                  <span className="error-message">{formErrors.feedback}</span>
                )}
              </div>
            </div>

            <div className="modal-activity-reject-footer ">
              <button
                type="button"
                className="btn btn-secondary cancel-btn"
                onClick={handleCloseRejectTaskModal}
              >
                Cancel
              </button>
              {loading ? (
                <button type="button" className="btn btn-primary" disabled>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="btn-spinner"
                  />
                  Rejecting...
                </button>
              ) : (
                <button type="submit" className="btn btn-primary submit-btn">
                  Reject Task
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default RejectTaskModal;
